import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, type JSX } from 'react'
import { useLocation } from 'react-router-dom'

import { isBaseProduct } from '@utils/AccountAccess/accountProductStatuses'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'

import {
  BaseProduct,
  TickmillProductType,
  getTickmillProductTypeName,
  isTickmillProductTypeCFD,
} from '@model/TickmillProductType'

import { useGuardedContext } from '@global/useGuardedContext'

import { useLocallyPersistedState } from '@hooks/useStorage'

import { AuthSessionContext } from './AuthContext'
import { useSessionEntity } from './EntityContext'

interface ChangeProductOptions {
  skipClientSideDataProductUpdate?: boolean
}

interface ProductReadContextImpl {
  product: BaseProduct
  hasInitializedProductState: boolean
  isDefaultCFDProductType(): boolean
}

interface ProductWriteContextImpl {
  changeProductContext: (
    product?: TickmillProductType,
    options?: ChangeProductOptions
  ) => BaseProduct
}

const ProductReadContext = createContext<ProductReadContextImpl | undefined>(undefined)
ProductReadContext.displayName = 'ProductReadContext'

const ProductWriteContext = createContext<ProductWriteContextImpl | undefined>(undefined)
ProductWriteContext.displayName = 'ProductWriteContext'

export function useProductReadContext(): ProductReadContextImpl {
  return useGuardedContext(ProductReadContext)
}

export function useProductWriteContext(): ProductWriteContextImpl {
  return useGuardedContext(ProductWriteContext)
}

interface Props {
  children: ReactNode
}

export const ProductKey = 'product'

export function ProductContextProvider({ children }: Props): JSX.Element {
  const entity = useSessionEntity()
  const [auth] = useContext(AuthSessionContext)
  const [persistedProductState, setProductState] = useLocallyPersistedState<TickmillProductType>(
    ProductKey,
    TickmillProductType.CFD
  )
  const productState = isBaseProduct(persistedProductState)
    ? persistedProductState
    : TickmillProductType.CFD
  const hasInitializedProductState = useRef(false)
  const location = useLocation()
  const inSignUpPath = location.pathname.includes('sign-up')

  useEffect(() => {
    if (persistedProductState !== productState) {
      setProductState(productState)
    }
  }, [persistedProductState, productState, setProductState])

  const readContext = useMemo<ProductReadContextImpl>(
    () => ({
      product: productState,
      hasInitializedProductState: hasInitializedProductState.current,
      isDefaultCFDProductType: () => productState === TickmillProductType.CFD,
    }),
    [productState]
  )

  useEffect(() => {
    if (!auth) {
      removeProductClientSide()
      if (productState !== TickmillProductType.CFD) {
        setProductState(TickmillProductType.CFD)
      }
      hasInitializedProductState.current = false
      return
    }

    if (inSignUpPath) {
      removeProductClientSide()
      hasInitializedProductState.current = false
      return
    }

    if (!hasInitializedProductState.current) {
      hasInitializedProductState.current = true
      applyProductClientSide(productState)
      return
    }

    applyProductClientSide(productState)
  }, [auth, productState, inSignUpPath, setProductState])

  const writeContext = useMemo<ProductWriteContextImpl>(() => {
    return {
      changeProductContext(p) {
        if (entity === TickmillCompaniesEnum.TICKMILL_UK) {
          const newProductState = resolveProductContextProduct(productState, p)

          if (newProductState !== productState) {
            setProductState(newProductState)
            applyProductClientSide(newProductState)
          }

          return newProductState
        }
        return productState
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productState, entity])

  return (
    <ProductReadContext.Provider value={readContext}>
      <ProductWriteContext.Provider value={writeContext}>{children}</ProductWriteContext.Provider>
    </ProductReadContext.Provider>
  )
}

const applyProductClientSide = (product: BaseProduct): void => {
  if (isTickmillProductTypeCFD(product)) {
    document.body.setAttribute(
      'data-product',
      getTickmillProductTypeName(TickmillProductType.CFD).toLowerCase()
    )
  } else {
    document.body.setAttribute(
      'data-product',
      getTickmillProductTypeName(TickmillProductType.ETD).toLowerCase()
    )
  }
}

const removeProductClientSide = (): void => {
  if (document.body.hasAttribute('data-product')) {
    document.body.removeAttribute('data-product')
  }
}

const resolveProductContextProduct = (
  currentProduct: BaseProduct,
  requestedProduct?: TickmillProductType
): BaseProduct => {
  if (
    requestedProduct === TickmillProductType.CFD ||
    requestedProduct === TickmillProductType.ETD
  ) {
    return requestedProduct
  }
  if (requestedProduct !== undefined) return currentProduct
  return currentProduct === TickmillProductType.CFD
    ? TickmillProductType.ETD
    : TickmillProductType.CFD
}
